/** Canonical project status values used for tab visibility. */
export enum ProjectDetailsStatus {
  Upcoming = 'upcoming',
  Ongoing = 'ongoing',
  OnHold = 'on-hold',
  Completed = 'completed',
  Pipeline = 'pipeline',
  Dormant = 'dormant',
  Unknown = 'unknown',
}

/** Roles that can access privileged project-detail tabs. */
export enum ProjectDetailsRole {
  Admin = 'admin',
  ProjectManager = 'project-manager',
  ProjectIncharge = 'project-incharge',
}

export const PROJECT_DETAILS_TAB_KEYS = {
  Summary: 'summary',
  ProjectSetup: 'project-setup',
  WorkPlan: 'work-plan',
  Allocation: 'allocation',
  ProjectTeam: 'project-team',
  Documents: 'documents',
  ProjectTimesheet: 'project-timesheet',
  Feedback: 'feedback',
  Discussion: 'discussion',
  Directory: 'directory',
  ProjectNotes: 'project-notes',
  ProjectScore: 'project-score',
  Insights: 'insights',
} as const;

export type ProjectDetailsTabKey =
  (typeof PROJECT_DETAILS_TAB_KEYS)[keyof typeof PROJECT_DETAILS_TAB_KEYS];

export interface ProjectDetailsTabContext {
  readonly status: ProjectDetailsStatus;
  readonly roles: ReadonlySet<ProjectDetailsRole>;
  readonly insightsFeatureEnabled: boolean;
  readonly discussionFeatureEnabled: boolean;
}

export interface ProjectDetailsTabDefinition {
  readonly key: ProjectDetailsTabKey;
  readonly heading: string;
  readonly activeRoute: string;
  readonly visible: (ctx: ProjectDetailsTabContext) => boolean;
}

function hasPrivilegedRole(ctx: ProjectDetailsTabContext): boolean {
  return (
    ctx.roles.has(ProjectDetailsRole.Admin) ||
    ctx.roles.has(ProjectDetailsRole.ProjectManager) ||
    ctx.roles.has(ProjectDetailsRole.ProjectIncharge)
  );
}

function isUpcoming(ctx: ProjectDetailsTabContext): boolean {
  return ctx.status === ProjectDetailsStatus.Upcoming;
}

function isCompleted(ctx: ProjectDetailsTabContext): boolean {
  return ctx.status === ProjectDetailsStatus.Completed;
}

/** Tabs visible for every user regardless of status (unless extended later). */
function alwaysVisible(): boolean {
  return true;
}

export const PROJECT_DETAILS_TAB_DEFINITIONS: readonly ProjectDetailsTabDefinition[] = [
  {
    key: PROJECT_DETAILS_TAB_KEYS.Summary,
    heading: 'Overview',
    activeRoute: 'summary',
    visible: (ctx) => !isUpcoming(ctx),
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.ProjectSetup,
    heading: 'Skill Configuration',
    activeRoute: 'project-setup',
    visible: (ctx) => isUpcoming(ctx) && hasPrivilegedRole(ctx),
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.WorkPlan,
    heading: 'Work Plan',
    activeRoute: 'work-plan',
    visible: (ctx) => isUpcoming(ctx) && hasPrivilegedRole(ctx),
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.Allocation,
    heading: 'Planning',
    activeRoute: 'allocation',
    visible: (ctx) => !isUpcoming(ctx),
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.ProjectTeam,
    heading: 'Team',
    activeRoute: 'project-team',
    visible: alwaysVisible,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.Documents,
    heading: 'Documents',
    activeRoute: 'documents',
    visible: alwaysVisible,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.ProjectTimesheet,
    heading: 'Timesheets',
    activeRoute: 'project-timesheet',
    visible: hasPrivilegedRole,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.Feedback,
    heading: 'Feedback',
    activeRoute: 'feedback',
    visible: alwaysVisible,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.Discussion,
    heading: 'Discussion',
    activeRoute: 'discussion',
    visible: (ctx) => ctx.discussionFeatureEnabled,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.Directory,
    heading: 'Directory',
    activeRoute: 'directory',
    visible: alwaysVisible,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.ProjectNotes,
    heading: 'Notes',
    activeRoute: 'project-notes',
    visible: alwaysVisible,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.ProjectScore,
    heading: 'Project Score',
    activeRoute: 'project-score',
    visible: isCompleted,
  },
  {
    key: PROJECT_DETAILS_TAB_KEYS.Insights,
    heading: 'Insights',
    activeRoute: 'insights',
    // Feature flag only; ResourceAllocationAccessService gates by Insights view permission.
    visible: (ctx) => ctx.insightsFeatureEnabled,
  },
] as const;

/** Normalizes API / UI status strings to `ProjectDetailsStatus`. */
export function normalizeProjectDetailsStatus(raw: unknown): ProjectDetailsStatus {
  const compact = String(raw ?? '')
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, '');

  if (compact === 'upcoming' || compact === 'upcomming') {
    return ProjectDetailsStatus.Upcoming;
  }
  if (compact === 'ongoing' || compact === 'active' || compact === 'inprogress') {
    return ProjectDetailsStatus.Ongoing;
  }
  if (compact === 'onhold') {
    return ProjectDetailsStatus.OnHold;
  }
  if (compact === 'completed') {
    return ProjectDetailsStatus.Completed;
  }
  if (compact === 'pipeline') {
    return ProjectDetailsStatus.Pipeline;
  }
  if (
    compact === 'dormant' ||
    compact === 'cancelled' ||
    compact === 'canceled' ||
    compact === 'lost'
  ) {
    return ProjectDetailsStatus.Dormant;
  }
  return ProjectDetailsStatus.Unknown;
}

export function resolveProjectDetailsRoles(params: {
  readonly isOrgAdmin: boolean;
  readonly userId: number | null;
  readonly project: {
    readonly projectInCharge?: { readonly id?: number };
    readonly projectLead?: { readonly id?: number };
    readonly projectManager?: { readonly id?: number };
  } | null;
}): ReadonlySet<ProjectDetailsRole> {
  const roles = new Set<ProjectDetailsRole>();
  if (params.isOrgAdmin) {
    roles.add(ProjectDetailsRole.Admin);
  }
  const uid = params.userId;
  const project = params.project;
  if (uid == null || !project) {
    return roles;
  }
  const picId = Number(project.projectInCharge?.id ?? project.projectLead?.id);
  const pmId = Number(project.projectManager?.id);
  if (Number.isFinite(picId) && uid === picId) {
    roles.add(ProjectDetailsRole.ProjectIncharge);
  }
  if (Number.isFinite(pmId) && uid === pmId) {
    roles.add(ProjectDetailsRole.ProjectManager);
  }
  return roles;
}

export function buildProjectDetailsTabContext(params: {
  readonly project: { readonly status?: unknown } | null;
  readonly fallbackStatus?: unknown;
  readonly roles: ReadonlySet<ProjectDetailsRole>;
  readonly insightsFeatureEnabled: boolean;
  readonly discussionFeatureEnabled: boolean;
}): ProjectDetailsTabContext {
  const status = normalizeProjectDetailsStatus(
    params.project?.status ?? params.fallbackStatus ?? '',
  );
  return {
    status,
    roles: params.roles,
    insightsFeatureEnabled: params.insightsFeatureEnabled,
    discussionFeatureEnabled: params.discussionFeatureEnabled,
  };
}

export function filterVisibleProjectDetailsTabs(
  definitions: readonly ProjectDetailsTabDefinition[],
  ctx: ProjectDetailsTabContext,
): ProjectDetailsTabDefinition[] {
  return definitions.filter((tab) => tab.visible(ctx));
}
